import { Edge, EdgeProps, Node } from '@xyflow/react'
import {
  RolodexEntryJson,
  RolodexEntryType,
} from 'types/rolodex/rolodexEntries'
import { parseString } from 'utils/parsingHelpers'

import { GeckoEntryBase } from './geckoEntry'

export type PfdLabelPosition = 'top' | 'bottom' | 'left' | 'right' | 'center'

export type PfdShapeStyle = 'outlined' | 'filled' | 'dashed' | 'dotted'

export type PfdNodeDataType = {
  label: string
  labelPosition?: PfdLabelPosition
  shapeColor?: string
  textColor?: string
  shapeStyle?: PfdShapeStyle
  metadata?: Record<string, string>
  link?: {
    entryType: string
    entryID: string
    siteID: string
  }
  assetLensLocations?: string[]
  datapoints?: string[]
  status?: string
  rotation?: number
  scale?: number
}

export type PfdEdgeDataType = {
  confirmedEdge?: boolean
  label?: string
  strokeWidth?: number
}

export type PfdEdgeProps = EdgeProps & {
  data: PfdEdgeDataType
}

export type PfdNode = Node<PfdNodeDataType>
export type PfdEdge = Edge<PfdEdgeDataType>

export type PfdData = {
  nodes: PfdNode[]
  edges: PfdEdge[]
}

export type PfdMetadata = {
  name: string
}

export type ProcessFlowDiagramJSON = {
  metadata: PfdMetadata
  diagram: PfdData
}

/**
 * A process flow diagram entry representing system diagrams and their connections.
 */
export class GeckoProcessFlowDiagram extends GeckoEntryBase<
  ProcessFlowDiagramJSON,
  {
    organization: string
    site: string
    system?: string
  }
> {
  private constructor(json: RolodexEntryJson) {
    // Coerce unknown data into the expected shape using parse helpers
    const metadata = json.data.metadata as PfdMetadata
    const diagram = json.data.diagram as PfdData

    super(
      json,
      {
        metadata,
        diagram,
      },
      {
        organization: parseString(json.tags.organization) ?? '',
        site: parseString(json.tags.site) ?? '',
        system: parseString(json.tags.system),
      },
      {}
    )

    // Ensure the type is explicitly set to process-flow-diagram
    ;(this as { type: RolodexEntryType }).type =
      RolodexEntryType.PROCESS_FLOW_DIAGRAM
  }

  public static CreateFromJSON(json: RolodexEntryJson) {
    return new GeckoProcessFlowDiagram(json)
  }
}
